import { DownloadOutlined } from "@ant-design/icons"
import { Button, Empty, Flex, Modal, Segmented, Table, Tag } from "antd"
import type { ColumnsType } from "antd/es/table"
import type { CSSProperties } from "react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import { downloadFilesAsZip } from "../../../utils/download-files-zip.ts"
import { budgetFormat } from "../../../utils/number/budget-format.ts"
import { FilePreviewContent } from "../../../components/FilePreviewModal/FilePreviewContent.tsx"
import {
  CONTRACT_STATUSES_OBJ,
  type ContractStatuses
} from "../../Contracts/constants/contract-statuses-keys.ts"
import {
  PROPOSAL_STATUSES_OBJ,
  type ProposalStatuses
} from "../../Proposals/constants/proposal-statuses-keys.ts"
import { BusinessDocumentStatusSummary } from "./BusinessDocumentStatusSummary.tsx"
import {
  useBusinessDocumentsContracts,
  useBusinessDocumentsProposals
} from "../hooks/useBusinessDocuments.ts"
import type {
  BusinessDocumentContract,
  BusinessDocumentProposal
} from "../types/business-documents.types.ts"

type BusinessDocumentsFilter = "all" | "proposals" | "contracts"

interface GlobalFilesBusinessDocumentsTabProps {
  search: string
  initialFilter: string | null
}

function workspacePath(leadId: string, isProject: boolean | null): string {
  return isProject
    ? RoutesPaths.project.view(leadId).overview
    : RoutesPaths.lead.view(leadId)
}

function WorkspaceCell({
  leadId,
  leadName,
  isProject
}: {
  leadId: string
  leadName: string | null
  isProject: boolean | null
}) {
  const { t } = useTranslation()
  const label = isProject
    ? t("globalFiles.relatedTo.project", "Project")
    : t("globalFiles.relatedTo.lead", "Lead")
  return (
    <Link to={workspacePath(leadId, isProject)}>
      {label} · {leadName || leadId}
    </Link>
  )
}

function CustomerCell({ name }: { name: string | null }) {
  if (!name) return <>—</>
  return <>{name}</>
}

/** Business Documents (EAD-615 Phase B) — the real `Proposal`/`Contract`
 * business records, not the generic file list narrowed by `document_type`.
 * "All" normalizes both into one shared column set and merges/sorts by
 * `updated`; the Proposals/Contracts tabs show each record's own richer,
 * type-specific columns and status-summary chips. */
export function GlobalFilesBusinessDocumentsTab({
  search,
  initialFilter
}: GlobalFilesBusinessDocumentsTabProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [filter, setFilter] = useState<BusinessDocumentsFilter>(
    initialFilter === "proposals" || initialFilter === "contracts"
      ? initialFilter
      : "all"
  )
  const [proposalStatus, setProposalStatus] = useState<string | null>(null)
  const [contractStatus, setContractStatus] = useState<string | null>(null)
  const [previewDoc, setPreviewDoc] = useState<{
    url: string
    name: string
  } | null>(null)

  const wantProposals = filter === "all" || filter === "proposals"
  const wantContracts = filter === "all" || filter === "contracts"

  const proposalsState = useBusinessDocumentsProposals({
    q: wantProposals ? search : "",
    status: filter === "proposals" ? proposalStatus : null
  })
  const contractsState = useBusinessDocumentsContracts({
    q: wantContracts ? search : "",
    status: filter === "contracts" ? contractStatus : null
  })

  const loading =
    (wantProposals && proposalsState.loading) ||
    (wantContracts && contractsState.loading)
  const failed =
    (wantProposals && proposalsState.failed) ||
    (wantContracts && contractsState.failed)

  const viewDocument = (
    attachment: { file_url: string; file_name: string } | null | undefined
  ) => {
    if (!attachment) return
    setPreviewDoc({ url: attachment.file_url, name: attachment.file_name })
  }

  // Only Proposals carry a downloadable attachment in this list payload — a
  // Contract's document lives behind its own page (`canViewDocument` there is
  // a navigation, not a file), so "Download All" can only ever zip Proposals.
  const downloadableProposals = wantProposals
    ? proposalsState.items.filter((row) => row.latest_attachment)
    : []
  const handleDownloadAll = () =>
    downloadFilesAsZip(
      downloadableProposals.map((row) => ({
        url: row.latest_attachment!.file_url,
        name: row.latest_attachment!.file_name
      })),
      t(
        "globalFiles.businessDocuments.downloadAllZipName",
        "Business Documents"
      )
    )

  const proposalColumns: ColumnsType<BusinessDocumentProposal> = useMemo(
    () => [
      {
        title: t("globalFiles.businessDocuments.columns.proposal", "Proposal"),
        key: "proposal_id",
        render: (_, row) => (
          <Link to={RoutesPaths.proposal.view(row.id)}>{row.proposal_id}</Link>
        )
      },
      {
        title: t("globalFiles.businessDocuments.columns.customer", "Customer"),
        key: "customer",
        render: (_, row) => <CustomerCell name={row.customer_name} />
      },
      {
        title: t(
          "globalFiles.businessDocuments.columns.leadProject",
          "Lead / Project"
        ),
        key: "workspace",
        render: (_, row) => (
          <WorkspaceCell
            leadId={row.lead_id}
            leadName={row.lead_name}
            isProject={row.is_project}
          />
        )
      },
      {
        title: t("globalFiles.businessDocuments.columns.status", "Status"),
        key: "status",
        render: (_, row) => {
          const meta = PROPOSAL_STATUSES_OBJ[row.status as ProposalStatuses]
          return <Tag style={meta?.style}>{row.status_display}</Tag>
        }
      },
      {
        title: t("globalFiles.businessDocuments.columns.value", "Value"),
        key: "value",
        render: (_, row) => budgetFormat(row.sales_price ?? row.total_amount)
      },
      {
        title: t("globalFiles.businessDocuments.columns.version", "Version"),
        key: "version",
        render: (_, row) => row.version_display
      },
      {
        title: t("globalFiles.businessDocuments.columns.updated", "Updated"),
        key: "updated",
        render: (_, row) => new Date(row.updated).toLocaleDateString()
      },
      {
        title: t("globalFiles.businessDocuments.columns.actions", "Actions"),
        key: "actions",
        render: (_, row) =>
          row.latest_attachment ? (
            <a onClick={() => viewDocument(row.latest_attachment)}>
              {t("globalFiles.businessDocuments.viewDocument", "View Document")}
            </a>
          ) : (
            <span style={{ color: "rgba(0,0,0,.35)" }}>
              {t("globalFiles.businessDocuments.noDocument", "No document yet")}
            </span>
          )
      }
    ],
    [t]
  )

  const contractColumns: ColumnsType<BusinessDocumentContract> = useMemo(
    () => [
      {
        title: t("globalFiles.businessDocuments.columns.contract", "Contract"),
        key: "contract_id",
        render: (_, row) => (
          <Link to={RoutesPaths.contract.view(row.id)}>{row.contract_id}</Link>
        )
      },
      {
        title: t("globalFiles.businessDocuments.columns.customer", "Customer"),
        key: "customer",
        render: (_, row) => <CustomerCell name={row.customer_name} />
      },
      {
        title: t(
          "globalFiles.businessDocuments.columns.leadProject",
          "Lead / Project"
        ),
        key: "workspace",
        render: (_, row) => (
          <WorkspaceCell
            leadId={row.lead_id}
            leadName={row.lead_name}
            isProject={row.is_project}
          />
        )
      },
      {
        title: t("globalFiles.businessDocuments.columns.status", "Status"),
        key: "status",
        render: (_, row) => {
          const meta = CONTRACT_STATUSES_OBJ[row.status as ContractStatuses]
          return <Tag style={meta?.style}>{row.status_display}</Tag>
        }
      },
      {
        title: t("globalFiles.businessDocuments.columns.value", "Value"),
        key: "value",
        render: (_, row) => budgetFormat(row.total_amount)
      },
      {
        title: t("globalFiles.businessDocuments.columns.version", "Version"),
        key: "version",
        render: (_, row) => row.version_display
      },
      {
        title: t("globalFiles.businessDocuments.columns.updated", "Updated"),
        key: "updated",
        render: (_, row) => new Date(row.updated).toLocaleDateString()
      },
      {
        title: t("globalFiles.businessDocuments.columns.actions", "Actions"),
        key: "actions",
        // The Contract list serializer carries no attachment object to
        // preview (only `submitted_attachment_is_uploaded`/`_source`
        // booleans) — the Contract's own page is where its document is
        // actually viewable, so that's what this links to rather than
        // faking a File Viewer integration the data doesn't support.
        render: (_, row) => (
          <a onClick={() => navigate(RoutesPaths.contract.view(row.id))}>
            {t("globalFiles.businessDocuments.viewDocument", "View Document")}
          </a>
        )
      }
    ],
    [t, navigate]
  )

  const renderBody = () => {
    if (loading) return <Table loading columns={[]} dataSource={[]} />
    if (failed)
      return (
        <Empty
          description={t(
            "globalFiles.allFiles.loadFailed",
            "Couldn't load files."
          )}
        />
      )

    if (filter === "proposals") {
      return (
        <>
          {proposalsState.summary && (
            <BusinessDocumentStatusSummary
              title={t("globalFiles.businessDocuments.proposals", "Proposals")}
              total={proposalsState.summary.total}
              selectedKey={proposalStatus}
              onSelectStatus={setProposalStatus}
              items={[
                {
                  key: "pending_review",
                  label: t(
                    "globalFiles.businessDocuments.pendingReview",
                    "Pending Review"
                  ),
                  count: proposalsState.summary.pending_review,
                  tone: "warn"
                },
                {
                  key: "approved",
                  label: t(
                    "globalFiles.businessDocuments.approved",
                    "Approved"
                  ),
                  count: proposalsState.summary.approved,
                  tone: "ok"
                },
                {
                  key: "other",
                  label: t("globalFiles.businessDocuments.other", "Other"),
                  count: proposalsState.summary.other
                }
              ]}
            />
          )}
          {proposalsState.items.length ? (
            <Table
              rowKey="id"
              columns={proposalColumns}
              dataSource={proposalsState.items}
              pagination={false}
            />
          ) : (
            <Empty
              description={t(
                "globalFiles.businessDocuments.emptyProposals",
                "No proposals match this filter yet."
              )}
            />
          )}
        </>
      )
    }

    if (filter === "contracts") {
      return (
        <>
          {contractsState.summary && (
            <BusinessDocumentStatusSummary
              title={t("globalFiles.businessDocuments.contracts", "Contracts")}
              total={contractsState.summary.total}
              selectedKey={contractStatus}
              onSelectStatus={setContractStatus}
              items={[
                {
                  key: "awaiting_signature",
                  label: t(
                    "globalFiles.businessDocuments.awaitingSignature",
                    "Awaiting Signature"
                  ),
                  count: contractsState.summary.awaiting_signature,
                  tone: "warn"
                },
                {
                  key: "signed",
                  label: t("globalFiles.businessDocuments.signed", "Signed"),
                  count: contractsState.summary.signed,
                  tone: "ok"
                },
                {
                  key: "other",
                  label: t("globalFiles.businessDocuments.other", "Other"),
                  count: contractsState.summary.other
                }
              ]}
            />
          )}
          {contractsState.items.length ? (
            <Table
              rowKey="id"
              columns={contractColumns}
              dataSource={contractsState.items}
              pagination={false}
            />
          ) : (
            <Empty
              description={t(
                "globalFiles.businessDocuments.emptyContracts",
                "No contracts match this filter yet."
              )}
            />
          )}
        </>
      )
    }

    // "All" — the two record types genuinely differ in shape (see
    // `business-documents.types.ts`'s own comment), so this normalizes only
    // what a shared table needs, and keeps the type-specific richness for
    // each dedicated tab above.
    type CombinedRow = {
      key: string
      kind: "proposal" | "contract"
      identifier: string
      viewPath: string
      customer_name: string | null
      lead_id: string
      lead_name: string | null
      is_project: boolean | null
      statusLabel: string
      statusStyle?: CSSProperties
      value: string | null
      version_display: string
      updated: string
      onViewDocument: () => void
      canViewDocument: boolean
    }

    const combined: CombinedRow[] = [
      ...proposalsState.items.map((row): CombinedRow => ({
        key: `proposal:${row.id}`,
        kind: "proposal",
        identifier: row.proposal_id,
        viewPath: RoutesPaths.proposal.view(row.id),
        customer_name: row.customer_name,
        lead_id: row.lead_id,
        lead_name: row.lead_name,
        is_project: row.is_project,
        statusLabel: row.status_display,
        statusStyle:
          PROPOSAL_STATUSES_OBJ[row.status as ProposalStatuses]?.style,
        value: row.sales_price ?? row.total_amount,
        version_display: row.version_display,
        updated: row.updated,
        onViewDocument: () => viewDocument(row.latest_attachment),
        canViewDocument: Boolean(row.latest_attachment)
      })),
      ...contractsState.items.map((row): CombinedRow => ({
        key: `contract:${row.id}`,
        kind: "contract",
        identifier: row.contract_id,
        viewPath: RoutesPaths.contract.view(row.id),
        customer_name: row.customer_name,
        lead_id: row.lead_id,
        lead_name: row.lead_name,
        is_project: row.is_project,
        statusLabel: row.status_display,
        statusStyle:
          CONTRACT_STATUSES_OBJ[row.status as ContractStatuses]?.style,
        value: row.total_amount,
        version_display: row.version_display,
        updated: row.updated,
        onViewDocument: () => navigate(RoutesPaths.contract.view(row.id)),
        canViewDocument: true
      }))
    ].sort(
      (a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime()
    )

    const combinedColumns: ColumnsType<CombinedRow> = [
      {
        title: t("globalFiles.businessDocuments.columns.document", "Document"),
        key: "identifier",
        render: (_, row) => (
          <>
            <Tag>
              {row.kind === "proposal"
                ? t("globalFiles.businessDocuments.proposal", "Proposal")
                : t("globalFiles.businessDocuments.contract", "Contract")}
            </Tag>
            <Link to={row.viewPath}>{row.identifier}</Link>
          </>
        )
      },
      {
        title: t("globalFiles.businessDocuments.columns.customer", "Customer"),
        key: "customer",
        render: (_, row) => <CustomerCell name={row.customer_name} />
      },
      {
        title: t(
          "globalFiles.businessDocuments.columns.leadProject",
          "Lead / Project"
        ),
        key: "workspace",
        render: (_, row) => (
          <WorkspaceCell
            leadId={row.lead_id}
            leadName={row.lead_name}
            isProject={row.is_project}
          />
        )
      },
      {
        title: t("globalFiles.businessDocuments.columns.status", "Status"),
        key: "status",
        render: (_, row) => <Tag style={row.statusStyle}>{row.statusLabel}</Tag>
      },
      {
        title: t("globalFiles.businessDocuments.columns.value", "Value"),
        key: "value",
        render: (_, row) => budgetFormat(row.value)
      },
      {
        title: t("globalFiles.businessDocuments.columns.version", "Version"),
        key: "version",
        render: (_, row) => row.version_display
      },
      {
        title: t("globalFiles.businessDocuments.columns.updated", "Updated"),
        key: "updated",
        render: (_, row) => new Date(row.updated).toLocaleDateString()
      },
      {
        title: t("globalFiles.businessDocuments.columns.actions", "Actions"),
        key: "actions",
        render: (_, row) =>
          row.canViewDocument ? (
            <a onClick={row.onViewDocument}>
              {t("globalFiles.businessDocuments.viewDocument", "View Document")}
            </a>
          ) : (
            <span style={{ color: "rgba(0,0,0,.35)" }}>
              {t("globalFiles.businessDocuments.noDocument", "No document yet")}
            </span>
          )
      }
    ]

    return combined.length ? (
      <Table
        rowKey="key"
        columns={combinedColumns}
        dataSource={combined}
        pagination={false}
      />
    ) : (
      <Empty
        description={t(
          "globalFiles.businessDocuments.empty",
          "No business documents match this filter yet."
        )}
      />
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap="small">
        <Segmented
          value={filter}
          onChange={(value) => setFilter(value as BusinessDocumentsFilter)}
          options={[
            {
              label: t("globalFiles.businessDocuments.all", "All"),
              value: "all"
            },
            {
              label: t("globalFiles.businessDocuments.proposals", "Proposals"),
              value: "proposals"
            },
            {
              label: t("globalFiles.businessDocuments.contracts", "Contracts"),
              value: "contracts"
            }
          ]}
        />
        <Button
          icon={<DownloadOutlined />}
          disabled={!downloadableProposals.length}
          onClick={handleDownloadAll}
          title={
            !downloadableProposals.length
              ? t(
                  "globalFiles.businessDocuments.downloadAllNoneAvailable",
                  "No downloadable documents in this view — Contracts open on their own page."
                )
              : undefined
          }
        >
          {t(
            "globalFiles.businessDocuments.downloadAll",
            "Download All Documents"
          )}
        </Button>
      </Flex>

      {renderBody()}

      {previewDoc && (
        <Modal
          open
          title={previewDoc.name}
          onCancel={() => setPreviewDoc(null)}
          footer={null}
          width={800}
        >
          <FilePreviewContent fileUrl={previewDoc.url} name={previewDoc.name} />
        </Modal>
      )}
    </div>
  )
}
